import { TRANSPARENT } from './types';

/** Grid-composition helpers: frames are built from reusable parts
 * (a torso stamped with per-phase legs) so animation stays coherent. */

export function blank(w: number, h: number): string[] {
  return Array.from({ length: h }, () => TRANSPARENT.repeat(w));
}

/** Overlay `part` onto `base` at (x, y); '.' in the part is see-through. */
export function stamp(base: string[], part: string[], x: number, y: number): string[] {
  const out = base.map((row) => row.split(''));
  part.forEach((partRow, py) => {
    const targetY = y + py;
    if (targetY < 0 || targetY >= out.length) return;
    for (let px = 0; px < partRow.length; px += 1) {
      const char = partRow[px]!;
      if (char === TRANSPARENT) continue;
      const targetX = x + px;
      if (targetX < 0 || targetX >= out[targetY]!.length) continue;
      out[targetY]![targetX] = char;
    }
  });
  return out.map((row) => row.join(''));
}

/** Mirror left-right (for a future left-facing walk). */
export function flipH(grid: string[]): string[] {
  return grid.map((row) => row.split('').reverse().join(''));
}

/** Shift a grid within its own bounds, dropping pixels that fall off. */
export function shift(grid: string[], dx: number, dy: number): string[] {
  const h = grid.length;
  const w = grid[0]?.length ?? 0;
  return stamp(blank(w, h), grid, dx, dy);
}

/** Build one row from [column, segment] parts — hand-placed pixels
 * without hand-counted padding, so widths can never drift. */
export function row(width: number, ...parts: [number, string][]): string {
  const out = Array.from({ length: width }, () => TRANSPARENT);
  for (const [x, segment] of parts) {
    for (let index = 0; index < segment.length; index += 1) {
      const char = segment[index]!;
      if (char === TRANSPARENT) continue;
      const target = x + index;
      if (target >= 0 && target < width) out[target] = char;
    }
  }
  return out.join('');
}

// ------------------------------------------------------------- cells
// Mutable char-matrix painting for structural art (buildings, limbs).

export type Cells = string[][];

export function toCells(grid: string[]): Cells {
  return grid.map((r) => r.split(''));
}

export function fromCells(cells: Cells): string[] {
  return cells.map((r) => r.join(''));
}

export function plot(cells: Cells, x: number, y: number, char: string, over = true): void {
  if (y < 0 || y >= cells.length || x < 0 || x >= (cells[0]?.length ?? 0)) return;
  if (!over && cells[y]![x] !== TRANSPARENT) return;
  cells[y]![x] = char;
}

export function rect(cells: Cells, x: number, y: number, w: number, h: number, char: string): void {
  for (let py = y; py < y + h; py += 1) {
    for (let px = x; px < x + w; px += 1) plot(cells, px, py, char);
  }
}

export function outlineRect(cells: Cells, x: number, y: number, w: number, h: number, char: string): void {
  for (let px = x; px < x + w; px += 1) {
    plot(cells, px, y, char);
    plot(cells, px, y + h - 1, char);
  }
  for (let py = y; py < y + h; py += 1) {
    plot(cells, x, py, char);
    plot(cells, x + w - 1, py, char);
  }
}

export function discFill(cells: Cells, cx: number, cy: number, r: number, char: string): void {
  for (let py = cy - r; py <= cy + r; py += 1) {
    for (let px = cx - r; px <= cx + r; px += 1) {
      const dx = px - cx;
      const dy = py - cy;
      if (dx * dx + dy * dy <= r * r + r * 0.5) plot(cells, px, py, char);
    }
  }
}

/** Thick line as horizontal runs per step — good for mostly-vertical strokes. */
export function thickLine(
  cells: Cells,
  from: readonly [number, number],
  to: readonly [number, number],
  width: number,
  char: string,
  over = true,
): void {
  const steps = Math.max(Math.abs(to[0] - from[0]), Math.abs(to[1] - from[1]), 1);
  for (let step = 0; step <= steps; step += 1) {
    const t = step / steps;
    const x = Math.round(from[0] + (to[0] - from[0]) * t);
    const y = Math.round(from[1] + (to[1] - from[1]) * t);
    for (let dx = 0; dx < width; dx += 1) {
      plot(cells, x - Math.floor(width / 2) + dx, y, char, over);
    }
  }
}

/** Wrap every non-transparent region edge with an outline char. */
export function autoOutline(cells: Cells, char = '0'): void {
  const h = cells.length;
  const w = cells[0]?.length ?? 0;
  const edges: [number, number][] = [];
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (cells[y]![x] !== TRANSPARENT) continue;
      const filled =
        (y > 0 && cells[y - 1]![x] !== TRANSPARENT && cells[y - 1]![x] !== char) ||
        (y < h - 1 && cells[y + 1]![x] !== TRANSPARENT && cells[y + 1]![x] !== char) ||
        (x > 0 && cells[y]![x - 1] !== TRANSPARENT && cells[y]![x - 1] !== char) ||
        (x < w - 1 && cells[y]![x + 1] !== TRANSPARENT && cells[y]![x + 1] !== char);
      if (filled) edges.push([x, y]);
    }
  }
  for (const [x, y] of edges) cells[y]![x] = char;
}
